import { useI18n } from "@/i18n/I18nContext";
import { SatelliteDish, PenLine, Ruler } from "lucide-react";

/**
 * Récapitulatif de ce qui va être enregistré, avec l'addition écrite en clair.
 *
 * Quand un chantier compte plusieurs bâtiments, le couvreur n'en voit qu'un à
 * la fois dans l'atelier : celui qu'il ajuste. Les autres sont hors de vue, et
 * au moment d'enregistrer il doit croire sur parole un total dont il ne voit
 * pas la composition. C'est le pire moment pour douter.
 *
 * D'où l'addition posée, terme par terme — « 2 797 + 420 + 180 = 3 397 » — et
 * l'origine de chaque mesure : ce qui vient du satellite et ce qui vient d'un
 * contour tracé à la main ne méritent pas la même confiance.
 *
 * Le total affiché est la somme des nombres AFFICHÉS, pas l'arrondi de la somme
 * exacte : une addition qui ne tombe pas juste à l'écran ruinerait précisément
 * la confiance qu'on cherche à donner. L'écart avec la valeur chiffrée reste
 * sous le pi² carré près.
 */
export default function TotalMesure({ batiments = [], activeId, onSelect }) {
  const { t, lang } = useI18n();
  const fr = lang === "fr";
  const es = lang === "es";
  if (!batiments.length) return null;

  const n = (v) => Math.round(v || 0);
  const fmt = (v) => n(v).toLocaleString(fr ? "fr-CA" : es ? "es-MX" : "en-CA");
  const totalToit = batiments.reduce((a, b) => a + n(b.aire), 0);
  const totalSol = batiments.reduce((a, b) => a + n(b.sol), 0);
  const multi = batiments.length > 1;

  const T = {
    titre: fr ? "Total mesuré" : es ? "Total medido" : "Measured total",
    batiments: fr ? "bâtiments" : es ? "edificios" : "buildings",
    satellite: "Sat Premium",
    trace: fr ? "tracé" : es ? "trazado" : "traced",
    dims: fr ? "dimensions" : es ? "dimensiones" : "dimensions",
    forfait: fr ? "forfait" : es ? "suma alzada" : "lump sum",
    horsCarre: fr
      ? "dont {n} pi² au forfait, chiffrés à part et exclus du calcul au carré."
      : es
        ? "de los cuales {n} pies² a suma alzada, cotizados aparte y fuera del cálculo por cuadrado."
        : "including {n} sq ft priced as a lump sum, quoted separately and outside the per-square calculation.",
    toit: fr ? "Surface de toit" : es ? "Superficie de techo" : "Roof surface",
    sol: fr ? "Surface au sol" : es ? "Superficie en planta" : "Footprint",
  };

  return (
    <div className="bg-surface border border-line rounded-xl p-6 shadow-carte" data-testid="total-mesure">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-ink-faint">{T.titre}</h3>
        {multi && (
          <span className="text-xs text-ink-faint">{batiments.length} {T.batiments}</span>
        )}
      </div>

      {/* Une ligne par bâtiment. Cliquable : c'est aussi le moyen le plus court
          de revenir sur celui qu'on veut corriger. */}
      <div className="space-y-1.5 mb-3">
        {batiments.map((b) => {
          const actif = b.id === activeId;
          return (
            <button
              key={b.id}
              onClick={() => onSelect && onSelect(b.id)}
              data-testid={`total-batiment-${b.id}`}
              className={`w-full flex items-center gap-2 text-sm text-left rounded-md px-2 py-1.5 transition-colors ${
                actif ? "bg-orange-500/10 ring-1 ring-orange-500/30" : "hover:bg-hover"
              }`}
            >
              {b.dimensions
                ? <Ruler className="h-3.5 w-3.5 shrink-0 text-ink-soft" />
                : b.satellite
                  ? <SatelliteDish className="h-3.5 w-3.5 shrink-0 text-cyan-400" />
                  : <PenLine className="h-3.5 w-3.5 shrink-0 text-brand-ink" />}
              <span className="truncate flex-1 min-w-0">
                <span className={actif ? "font-semibold" : ""}>{b.label}</span>
                <span className="text-ink-faint text-xs">
                  {" · "}{b.dimensions ? T.dims : b.satellite ? T.satellite : T.trace}
                  {" · "}{b.pitchLabel}
                  {b.forfait > 0 && (
                    <span className="text-brand-ink"> · {T.forfait} {b.forfait.toLocaleString()} $</span>
                  )}
                </span>
              </span>
              <span className="font-medium whitespace-nowrap">
                {fmt(b.aire)} {t("units.sqft")}
              </span>
            </button>
          );
        })}
      </div>

      {/* L'addition posée : c'est elle qui permet de vérifier d'un coup d'œil
          qu'aucun bâtiment n'est oublié ni compté deux fois. */}
      <div className="border-t border-line pt-3 space-y-2">
        {multi && (
          <div className="text-xs text-ink-faint break-words" data-testid="equation-toit">
            {batiments.map((b) => fmt(b.aire)).join("  +  ")}
            {"  =  "}
            <span className="text-ink font-semibold">{fmt(totalToit)} {t("units.sqft")}</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-ink-soft">{T.toit}</span>
          <span className="font-bold text-base" data-testid="total-toit">
            {fmt(totalToit)} {t("units.sqft")}
          </span>
        </div>
        {/* Un bâtiment au forfait est mesuré comme les autres, mais il ne
            traverse pas le calcul au carré. Sans cette ligne, le total mesuré
            et la surface chiffrée diffèreraient sans explication. */}
        {batiments.some((b) => b.forfait > 0) && (
          <p className="text-[11px] text-ink-faint" data-testid="note-forfait">
            {T.horsCarre.replace("{n}", fmt(batiments.filter((b) => b.forfait > 0)
              .reduce((a, b) => a + n(b.aire), 0)))}
          </p>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-ink-soft">{T.sol}</span>
          <span className="font-medium" data-testid="total-sol">
            {fmt(totalSol)} {t("units.sqft")}
          </span>
        </div>
      </div>
    </div>
  );
}
